import {
  AdminApiError,
  type AdminClient,
  type ChainBet,
  type ChainMovement,
  type ChainTrace,
  type PlayerDetail,
  type PlayerHistorySummary,
} from '@apx/admin-sdk';
import { showToast } from '../toast.js';
import { askConfirm, customModal, html } from '../modal.js';
import { fmtDateTime, fmtMoney, fmtNumber } from '../format.js';
import { moneyStep, moneyUnitHint, readMoney } from '../money-input.js';
import { betTypeLabel, formatResult } from './trace-format.js';
import { icon } from '../icons.js';
import { resolveBrowserTz, tzSelectOptions, REPORT_TZ_STORAGE_KEY } from './reports-shared.js';

export interface PlayerDetailPageDeps {
  client: AdminClient;
  playerId: string;
  /** Permission probe from /v1/me. Actions the principal cannot perform are hidden. */
  can: (permission: string) => boolean;
  /** When set, the page renders inside that integrator's scope: the back-link
   *  returns to the integrator's Player list (keeping the integrator sidebar). */
  integratorId?: string;
}

export async function renderPlayerDetailPage(
  root: HTMLElement,
  deps: PlayerDetailPageDeps,
): Promise<void> {
  const backHref = deps.integratorId
    ? `/integrators/${encodeURIComponent(deps.integratorId)}/players`
    : '/players';
  // Render all timestamps in the operator's chosen timezone — the SAME sticky
  // tz the P/L report uses (localStorage), so the two pages read consistently
  // instead of silent browser-local. Updated in place by the tz picker below.
  let tz = localStorage.getItem(REPORT_TZ_STORAGE_KEY) || resolveBrowserTz();
  // The player's currency (fixed at registration). Every amount on this page —
  // balance, stakes, movements, totals — is an integer count of its minor
  // unit, and is shown in it. Set as soon as the detail loads.
  let cur = '';
  root.innerHTML = `
    <h1 class="page-title">Player detail</h1>
    <p class="page-sub">
      <a href="${backHref}" style="color:var(--accent-2);text-decoration:none">← Back to Players</a>
      &middot; <code>${escapeHtml(deps.playerId)}</code>
    </p>
    <div id="pd-body"><p class="muted">Loading…</p></div>
  `;
  const bodyEl = root.querySelector<HTMLElement>('#pd-body')!;

  async function refresh(): Promise<void> {
    try {
      const detail = await deps.client.fetchPlayerDetail(deps.playerId);
      cur = detail.player.currency;
      bodyEl.innerHTML = renderBody(detail);
      bindActions(detail);
      void loadHistory(detail);
    } catch (err) {
      bodyEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError
          ? `${escapeHtml(err.code)}: ${escapeHtml(err.message)}`
          : escapeHtml(String(err))
      }</p>`;
    }
  }

  function renderBody(d: PlayerDetail): string {
    const p = d.player;
    // Every game the player has played (server-provided) — falls back to the
    // recent-bets window only if an older API omits `games`.
    const gameOptions = [
      ...new Set(d.games ?? (d.recentBets ?? []).map((b) => b.gameId)),
    ].sort();
    const tzOptionsHtml = tzSelectOptions(resolveBrowserTz())
      .map((o) => `<option value="${escapeHtml(o.value)}"${o.value === tz ? ' selected' : ''}>${escapeHtml(o.label)}</option>`)
      .join('');
    return `
      <div style="display:flex;flex-direction:column;gap:16px">
      <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;align-items:stretch">
        <div class="card" style="margin-bottom:0">
          <h2>Profile</h2>
          <dl class="kv">
            <dt>ID</dt><dd><code>${escapeHtml(p.id)}</code></dd>
            <dt>Integrator</dt><dd><code>${escapeHtml(p.integratorId)}</code></dd>
            <dt>Integrator ref</dt><dd>${p.integratorPlayerRef ? `<code>${escapeHtml(p.integratorPlayerRef)}</code>` : '—'}</dd>
            <dt>Currency</dt><dd><code>${escapeHtml(p.currency)}</code> <span class="muted" style="font-size:11px">fixed at registration</span></dd>
            <dt>Username</dt><dd>${p.username ? escapeHtml(p.username) : '<span class="muted">(none)</span>'}</dd>
            <dt>Anonymous</dt><dd><span class="pill ${p.isAnonymous ? '' : 'live'}">${p.isAnonymous ? 'yes' : 'no'}</span></dd>
            <dt>Status</dt><dd><span class="pill ${p.banned ? 'warn' : 'live'}">${p.banned ? 'banned' : 'active'}</span></dd>
            <dt>Sound enabled</dt><dd>${p.settings?.soundEnabled ?? '—'}</dd>
            <dt>Music enabled</dt><dd>${p.settings?.musicEnabled ?? '—'}</dd>
            <dt>Locale</dt><dd>${p.settings?.locale ?? '—'}</dd>
            <dt>Created</dt><dd>${fmtDateTime(p.createdAt, tz)}</dd>
          </dl>
          <div class="row" style="margin-top:14px;flex-wrap:wrap">
            ${
              deps.can('player.ban')
                ? `<button id="pd-toggle-anon" data-current="${p.isAnonymous}">${p.isAnonymous ? 'Unmark anonymous' : 'Mark anonymous'}</button>
                   <button id="pd-toggle-ban" data-current="${p.banned}" class="ghost">${p.banned ? 'Unban' : 'Ban'}</button>`
                : ''
            }
            ${
              // PLAYER_BALANCE_ADJUST moves real money and is provider-only —
              // it is not in the tenant-grantable catalogue at all.
              deps.can('player.balance.adjust')
                ? `<button id="pd-adjust" class="ghost">Adjust balance</button>`
                : ''
            }
          </div>
        </div>
        <div class="card" style="margin-bottom:0">
          <h2>
            Balance
            ${
              (d.driftCount ?? 0) > 0
                ? `<a href="/wallet-drift?playerId=${encodeURIComponent(p.id)}" style="margin-left:8px;text-decoration:none"><span class="pill warn" title="Unresolved drift rows for this player">drift × ${d.driftCount}</span></a>`
                : ''
            }
          </h2>
          <p class="muted" style="font-size:12px;margin:-4px 0 12px">
            Cached balance (last seen), in the player's currency. Fetch live shows the
            integrator's canonical balance right now — a read only, it does
            not update the cache. The cached value refreshes on the next
            bet/settle. Source-of-truth is the integrator wallet.
          </p>
          ${(() => {
            // A player holds exactly one currency (fixed at registration), so a
            // single WalletShadow row → one balance line under p.currency.
            const w = d.wallets[0];
            const credits = w ? fmtMoney(w.creditBalance, p.currency) : '—';
            const updated = w ? fmtDateTime(w.updatedAt, tz) : '<span class="muted">never</span>';
            return `<table>
                  <thead><tr><th>Currency</th><th style="text-align:right">Balance</th><th>Updated</th><th></th></tr></thead>
                  <tbody><tr data-fiat-row="${escapeHtml(p.currency)}">
                    <td><code>${escapeHtml(p.currency)}</code></td>
                    <td class="pd-credits" style="text-align:right;font-size:18px;color:var(--accent);font-weight:600;font-variant-numeric:tabular-nums">${credits}</td>
                    <td class="pd-updated">${updated}</td>
                    <td style="text-align:right"><button class="ghost pd-refresh" data-fiat="${escapeHtml(p.currency)}">Fetch live</button></td>
                  </tr></tbody>
                </table>`;
          })()}
        </div>
      </div>

      <div class="card">
        <div class="detail-card-head" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px">
          <div>
            <h2>Game history</h2>
            <span class="muted">Rounds the player bet on, newest first. Expand a round to see each bet's wallet movements — <code>debit</code> (stake), <code>win</code> / <code>jackpot</code> (payouts on settle), <code>rollback</code> (reversal on void). <strong>Accept status</strong> sits beside the outcome: on a <code>VOID</code> bet, <code>cancelled</code> means the player undid it and <code>confirmed</code> means an admin refunded it. <strong>Integrity</strong> shows whether each movement is <em>sealed</em> into the append-only audit chain (tamper-evident); click it to verify in the round trace.</span>
          </div>
          <a id="pd-history-csv" href="${escapeHtml(deps.client.playerHistoryCsvUrl(p.id))}"
             download="apx-player-${escapeHtml(p.id)}-history.csv"
             class="ghost" style="padding:6px 12px;text-decoration:none;white-space:nowrap">Download CSV</a>
        </div>
        <div class="row pd-history-filters">
          <select id="pd-h-game" class="players-filter-select" title="Game">
            <option value="">All games</option>
            ${gameOptions.map((g) => `<option value="${escapeHtml(g)}">${escapeHtml(g)}</option>`).join('')}
          </select>
          <select id="pd-h-window" class="players-filter-select" title="Time window">
            <option value="">All time</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <select id="pd-h-tz" class="players-filter-select" title="Timezone (times below shown in this zone)">
            ${tzOptionsHtml}
          </select>
        </div>
        <div id="pd-history-summary" class="pd-history-summary"></div>
        <div id="pd-history"><p class="muted">Loading…</p></div>
      </div>
      </div>
    `;
  }

  /** Sum of win + jackpot movements minus debit movements across a round's
   *  bets — the player's net credit swing for that round. */
  function roundNet(round: ChainTrace): bigint {
    let net = 0n;
    for (const bet of round.bets) {
      for (const m of bet.movements) {
        let amount: bigint;
        try {
          amount = BigInt(m.creditAmount);
        } catch {
          continue;
        }
        if (m.kind === 'win' || m.kind === 'jackpot' || m.kind === 'rollback') net += amount;
        else if (m.kind === 'debit') net -= amount;
      }
    }
    return net;
  }

  /** Round lifecycle state → compact pill. `finished` = settled/closed,
   *  `showing_result` = result revealed but round still on screen. */
  function roundStatePill(state: string): string {
    const label = state === 'finished' ? 'settled' : state === 'showing_result' ? 'revealing' : state;
    const cls = state === 'finished' ? 'live' : state === 'showing_result' ? '' : 'warn';
    return `<span class="pill ${cls}" style="font-size:10px" title="Round state: ${escapeHtml(state)}">${escapeHtml(label)}</span>`;
  }

  function verdictPill(verdict?: 'VALID' | 'INVALID' | 'UNVERIFIABLE'): string {
    if (!verdict) return '<span class="muted">—</span>';
    const color =
      verdict === 'VALID'
        ? 'var(--success)'
        : verdict === 'UNVERIFIABLE'
          ? 'var(--warn, #fde047)'
          : 'var(--error)';
    return `<span style="color:${color};font-weight:600">${escapeHtml(verdict)}</span>`;
  }

  function kindColor(kind: ChainMovement['kind']): string {
    switch (kind) {
      case 'debit':
        return 'var(--error)';
      case 'win':
        return 'var(--success)';
      case 'jackpot':
        return 'var(--accent-2)';
      case 'rollback':
        return 'var(--warn)';
      default:
        return 'var(--muted, #888)';
    }
  }

  function movementsTable(movements: ChainMovement[], traceHref: string): string {
    if (movements.length === 0) return '<p class="muted">No wallet movements recorded.</p>';
    return `<table>
      <thead><tr>
        <th>Kind</th>
        <th style="text-align:right">Amount (${escapeHtml(cur)})</th>
        <th>Status</th>
        <th>When</th>
        <th>Integrity</th>
      </tr></thead>
      <tbody>${movements
        .map((m) => integrityRow(m, traceHref))
        .join('')}</tbody>
    </table>`;
  }

  /**
   * "Sealed" = the movement's hash has been stamped into the append-only wallet
   * audit chain, so any later edit/reorder breaks the chain (tamper-evident).
   * "Pending seal" = written but the background sealing worker hasn't stamped it
   * yet (normal for a just-now movement) — not an error.
   * The cell links to the round's full trace where the chain can be verified.
   */
  function integrityRow(m: ChainMovement, traceHref: string): string {
    const verifyHref = `${traceHref}#wtx-${encodeURIComponent(m.walletTxId)}`;
    const integrity = m.sealed
      ? `<a href="${verifyHref}" class="integrity integrity-sealed" title="Hash sealed into the append-only audit chain — tamper-evident. Click to verify in the round trace.">${icon('lock', { size: 13 })}<span>sealed</span></a>`
      : `<a href="${verifyHref}" class="integrity integrity-pending" title="Written, but the audit-chain sealing worker hasn't stamped it yet (normal for a fresh movement). Click to open the round trace.">${icon('unlock', { size: 13 })}<span>pending seal</span></a>`;
    return `<tr>
      <td><span class="tag" style="background:${kindColor(m.kind)};color:#fff;padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600">${escapeHtml(m.kind)}</span></td>
      <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(m.creditAmount, cur)}</td>
      <td><code>${escapeHtml(m.status)}</code></td>
      <td><span class="muted">${escapeHtml(fmtDateTime(m.createdAt, tz))}</span></td>
      <td>${integrity}</td>
    </tr>`;
  }

  function betBlock(b: ChainBet, traceHref: string): string {
    const outcomeClass = b.outcome === 'WIN' ? 'live' : b.outcome === 'VOID' ? 'warn' : '';
    // Accept status sits directly under outcome: the two are orthogonal, and
    // only the pair says what a row is (PENDING + confirmed is a live bet,
    // PENDING + rejected a debit the integrator declined).
    const acceptClass = b.acceptStatus === 'confirmed' ? '' : 'warn';
    return `<div style="border:1px solid var(--border);border-radius:6px;padding:10px 12px;margin-top:10px">
      <div class="kv">
        <dt>Pick</dt><dd><code>${escapeHtml(betTypeLabel(b.betType))}</code></dd>
        <dt>Stake</dt><dd style="font-variant-numeric:tabular-nums">${fmtMoney(b.stake, cur)} <span class="muted" style="font-size:11px">${escapeHtml(cur)}</span></dd>
        <dt>Outcome</dt><dd><span class="pill ${outcomeClass}">${escapeHtml(b.outcome)}</span></dd>
        <dt>Accept status</dt><dd>${b.acceptStatus ? `<span class="pill ${acceptClass}"${acceptClass ? '' : ' style="opacity:.6"'}>${escapeHtml(b.acceptStatus)}</span>` : '<span class="muted">—</span>'}</dd>
      </div>
      <h4 style="margin:12px 0 8px">Movements (${b.movements.length})</h4>
      ${movementsTable(b.movements, traceHref)}
    </div>`;
  }

  function roundRow(round: ChainTrace, integratorId: string): string {
    const r = round.round;
    const result = escapeHtml(formatResult(r.gameId, r.resultData));
    const net = roundNet(round);
    const netColor = net > 0n ? 'var(--success)' : net < 0n ? 'var(--error)' : 'var(--text-dim)';
    const when = r.endedAt ? fmtDateTime(r.endedAt, tz) : '<span class="muted">—</span>';
    const traceHref = `/integrators/${encodeURIComponent(integratorId)}/transaction-trace?id=${encodeURIComponent(r.roundId)}`;
    return `<details style="border-bottom:1px solid var(--border)">
      <summary style="cursor:pointer;list-style:none;display:flex;align-items:center;gap:12px;padding:10px 4px;font-size:13px">
        <a href="${escapeHtml(traceHref)}" title="${escapeHtml(r.roundId)}"
           style="color:var(--accent-2);text-decoration:none;font-family:var(--mono,monospace)">${escapeHtml(r.roundId.slice(-8))}</a>
        <code style="font-size:11px">${escapeHtml(r.gameId)}</code>
        ${roundStatePill(r.state)}
        <span class="muted" style="white-space:nowrap;font-size:12px">${when}</span>
        <span style="flex:1">${result}</span>
        <span style="color:${netColor};font-weight:600;font-variant-numeric:tabular-nums">${fmtMoney(net, cur, { signed: true })}</span>
        ${verdictPill(r.verifyVerdict)}
        <button data-view-report="${escapeHtml(r.roundId)}" title="Open round report"
          style="background:none;border:none;padding:4px 6px;border-radius:4px;cursor:pointer;color:var(--text-dim);display:inline-flex;align-items:center;flex-shrink:0"
        ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></button>
      </summary>
      <div style="padding:0 4px 12px">
        ${
          round.bets.length === 0
            ? '<p class="muted">No bets on this round.</p>'
            : round.bets.map((b) => betBlock(b, traceHref)).join('')
        }
      </div>
    </details>`;
  }

  async function loadHistory(d: PlayerDetail): Promise<void> {
    const host = bodyEl.querySelector<HTMLElement>('#pd-history');
    const summaryEl = bodyEl.querySelector<HTMLElement>('#pd-history-summary');
    const gameEl = bodyEl.querySelector<HTMLSelectElement>('#pd-h-game');
    const windowEl = bodyEl.querySelector<HTMLSelectElement>('#pd-h-window');
    const tzEl = bodyEl.querySelector<HTMLSelectElement>('#pd-h-tz');
    const csvEl = bodyEl.querySelector<HTMLAnchorElement>('#pd-history-csv');
    if (!host || !gameEl || !windowEl) return;
    const integratorId = d.player.integratorId;

    // Tiles come from the server aggregate over the WHOLE filter set (not the
    // loaded page); `loadedRounds` only tracks how many rows are on screen so
    // the footer can say "Showing X of Y".
    let totals: PlayerHistorySummary | null = null;
    let loadedRounds = 0;
    let lastNextCursor: string | null = null;
    let firstPageLoaded = false;

    function windowFrom(): string | undefined {
      const days: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30 };
      const n = days[windowEl!.value];
      return n ? new Date(Date.now() - n * 86_400_000).toISOString() : undefined;
    }

    function filters(): { gameId?: string; from?: string } {
      return { gameId: gameEl!.value || undefined, from: windowFrom() };
    }

    function renderSummary(): void {
      if (!summaryEl) return;
      const card = (label: string, value: string, color?: string): string =>
        `<div class="stat-card"><div class="stat-card-label">${label}</div><div class="stat-card-value"${color ? ` style="color:${color}"` : ''}>${value}</div></div>`;
      if (!totals) {
        summaryEl.innerHTML = `${card('Rounds', '…')}${card('Wagered', '…')}${card('Won', '…')}${card('Net', '…')}`;
        return;
      }
      let net = 0n;
      try {
        net = BigInt(totals.net);
      } catch {
        net = 0n;
      }
      const netColor = net > 0n ? 'var(--success)' : net < 0n ? 'var(--error)' : 'var(--text-dim)';
      summaryEl.innerHTML = `
        ${card('Rounds', fmtNumber(totals.totalRounds))}
        ${card(`Wagered (${escapeHtml(cur)})`, fmtMoney(totals.wagered, cur))}
        ${card(`Won (${escapeHtml(cur)})`, fmtMoney(totals.won, cur))}
        ${card(`Net (${escapeHtml(cur)})`, fmtMoney(totals.net, cur, { signed: true }), netColor)}
      `;
    }

    function updateCsvHref(): void {
      if (csvEl) csvEl.href = deps.client.playerHistoryCsvUrl(deps.playerId, filters());
    }

    const hostEl: HTMLElement = host;

    // Capture-phase delegation handles [data-view-report] eye buttons inside
    // <summary> elements — capture runs before the <details> toggle fires,
    // so stopPropagation() here prevents the panel from opening/closing.
    hostEl.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-view-report]');
      if (!btn) return;
      e.stopPropagation();
      const roundId = btn.dataset.viewReport!;
      btn.setAttribute('disabled', '');
      deps.client.getRoundReportUrl(roundId).then(({ reportUrl }) => {
        window.open(reportUrl, '_blank', 'noopener');
      }).catch((err: unknown) => {
        showToast(err instanceof Error ? err.message : String(err), 'error');
      }).finally(() => {
        btn.removeAttribute('disabled');
      });
    }, true /* capture */);

    const rowsEl = (): HTMLElement => hostEl.querySelector<HTMLElement>('#pd-history-rows')!;
    const footEl = (): HTMLElement => hostEl.querySelector<HTMLElement>('#pd-history-foot')!;

    // Re-rendered by BOTH the page loader and the totals fetch (whichever lands
    // second fills in the true "of Y" count). Guarded until the first page
    // resolves so a fast totals response can't paint a premature footer.
    function renderFooter(): void {
      if (!firstPageLoaded) return;
      const foot = footEl();
      if (loadedRounds === 0) {
        foot.innerHTML = '<p class="muted">No game history for these filters.</p>';
        return;
      }
      const total = totals ? totals.totalRounds : loadedRounds;
      const shown = `<span class="muted" style="font-size:12px">Showing ${loadedRounds.toLocaleString()} of ${total.toLocaleString()} rounds</span>`;
      if (lastNextCursor) {
        foot.innerHTML = `<div class="row" style="margin-top:12px;align-items:center;gap:12px">${shown}<button id="pd-history-more" class="ghost">Load more</button></div>`;
        foot
          .querySelector<HTMLButtonElement>('#pd-history-more')
          ?.addEventListener('click', () => void loadPage(lastNextCursor ?? undefined));
      } else {
        foot.innerHTML = `<div class="row" style="margin-top:12px">${shown}</div>`;
      }
    }

    async function loadPage(cursor?: string): Promise<void> {
      footEl().innerHTML = '<p class="muted">Loading…</p>';
      try {
        const res = await deps.client.getPlayerHistory(deps.playerId, {
          ...filters(),
          cursor,
          limit: 25,
        });
        loadedRounds += res.rounds.length;
        lastNextCursor = res.nextCursor;
        firstPageLoaded = true;
        rowsEl().insertAdjacentHTML(
          'beforeend',
          res.rounds.map((round) => roundRow(round, integratorId)).join(''),
        );
        renderFooter();
      } catch (err) {
        firstPageLoaded = true;
        footEl().innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
      }
    }

    async function fetchTotals(): Promise<void> {
      try {
        totals = await deps.client.getPlayerHistorySummary(deps.playerId, filters());
      } catch {
        totals = null; // tiles fall back to "…"; footer falls back to loaded count
      }
      renderSummary();
      renderFooter();
    }

    async function reload(): Promise<void> {
      totals = null;
      loadedRounds = 0;
      lastNextCursor = null;
      firstPageLoaded = false;
      updateCsvHref();
      renderSummary();
      hostEl.innerHTML = '<div id="pd-history-rows"></div><div id="pd-history-foot"></div>';
      await Promise.all([fetchTotals(), loadPage()]);
    }

    gameEl.addEventListener('change', () => void reload());
    windowEl.addEventListener('change', () => void reload());
    // Timezone change re-renders the whole page (profile/balance/round times all
    // shift) and persists the choice so it's shared with the P/L report.
    tzEl?.addEventListener('change', () => {
      tz = tzEl.value;
      localStorage.setItem(REPORT_TZ_STORAGE_KEY, tz);
      void refresh();
    });
    await reload();
  }

  function bindActions(d: PlayerDetail): void {
    const p = d.player;
    bodyEl.querySelector<HTMLButtonElement>('#pd-toggle-anon')?.addEventListener('click', async () => {
      try {
        await deps.client.updatePlayerSettings(p.id, { isAnonymous: !p.isAnonymous });
        showToast('Anonymity flipped.', 'success');
        await refresh();
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
    bodyEl.querySelector<HTMLButtonElement>('#pd-toggle-ban')?.addEventListener('click', async () => {
      if (!p.banned && !(await askConfirm('Ban this player? They will be locked out immediately.'))) return;
      try {
        await deps.client.updatePlayerSettings(p.id, { banned: !p.banned });
        showToast(p.banned ? 'Unbanned.' : 'Banned.', 'success');
        await refresh();
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
    bodyEl.querySelectorAll<HTMLButtonElement>('.pd-refresh').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const fiat = btn.dataset.fiat!;
        const row = bodyEl.querySelector<HTMLElement>(`[data-fiat-row="${fiat.replace(/"/g, '\\"')}"]`);
        if (!row) return;
        btn.disabled = true;
        const original = btn.textContent;
        btn.textContent = 'Fetching…';
        try {
          const res = await deps.client.refreshPlayerBalance(p.id, fiat);
          const credEl = row.querySelector<HTMLElement>('.pd-credits');
          const updEl = row.querySelector<HTMLElement>('.pd-updated');
          // The integrator answers in the currency it was asked for; the
          // amount is minor units of that currency.
          const liveCur = res.fiatCurrency || fiat;
          if (credEl) credEl.textContent = fmtMoney(res.creditBalance, liveCur);
          if (updEl) updEl.textContent = `live · ${fmtDateTime(res.fetchedAt)}`;
          showToast(`Live balance: ${fmtMoney(res.creditBalance, liveCur)} ${liveCur}`, 'success');
        } catch (err) {
          showToast(`Integrator unreachable: ${describe(err)}`, 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = original ?? 'Fetch live';
        }
      });
    });

    bodyEl.querySelector<HTMLButtonElement>('#pd-adjust')?.addEventListener('click', async () => {
      const parsed = await customModal<{
        mode: 'credit' | 'debit' | 'reconcile_manual';
        creditAmount: string;
        reason: string;
      }>({
        title: 'Adjust player balance',
        confirmLabel: 'Submit',
        body: html`
          <div style="display:flex;flex-direction:column;gap:14px">
            <fieldset style="border:1px solid var(--border);border-radius:6px;padding:10px 12px;display:flex;flex-direction:column;gap:6px">
              <legend style="padding:0 6px;font-size:11px;color:var(--muted)">Mode</legend>
              <label style="display:flex;gap:8px;align-items:flex-start;font-size:13px">
                <input type="radio" name="pd-adj-mode" value="credit" checked />
                <span><strong>Credit to player</strong> — calls integrator <code>/credit</code> w/ admin requestId. Adds the amount to the integrator wallet.</span>
              </label>
              <label style="display:flex;gap:8px;align-items:flex-start;font-size:13px">
                <input type="radio" name="pd-adj-mode" value="debit" />
                <span><strong>Debit from player</strong> — calls integrator <code>/debit</code>. Removes the amount.</span>
              </label>
              <label style="display:flex;gap:8px;align-items:flex-start;font-size:13px">
                <input type="radio" name="pd-adj-mode" value="reconcile_manual" />
                <span><strong>Manual reconcile</strong> — audit row only, NO integrator call. Used when apx + integrator disagree and ops is settling out-of-band.</span>
              </label>
            </fieldset>
            <label style="display:flex;flex-direction:column;gap:4px">
              <span class="muted" style="font-size:11px">Amount (${moneyUnitHint(p.currency)})</span>
              <input id="pd-adj-amount" type="number" min="${moneyStep(p.currency)}" step="${moneyStep(p.currency)}" placeholder="${moneyStep(p.currency) === '1' ? 'e.g. 50000' : 'e.g. 50.00'}" />
            </label>
            <label style="display:flex;flex-direction:column;gap:4px">
              <span class="muted" style="font-size:11px">Reason (audit-logged)</span>
              <input id="pd-adj-reason" type="text" placeholder="e.g. tournament prize, support credit" />
            </label>
            <div class="muted" style="font-size:11px">
              Idempotent — admin-issued <code>requestId</code> is stamped automatically.
              All modes write an <code>AdminAudit</code> row.
            </div>
          </div>
        `,
        parseOnConfirm: (root) => {
          const mode = (root.querySelector<HTMLInputElement>('input[name="pd-adj-mode"]:checked')?.value
            ?? 'credit') as 'credit' | 'debit' | 'reconcile_manual';
          const reason = root.querySelector<HTMLInputElement>('#pd-adj-reason')!.value.trim();
          // Typed in the player's currency ("50.00" USD); sent as the integer
          // count of its minor unit the API takes (5000 cents).
          const amount = readMoney(root.querySelector<HTMLInputElement>('#pd-adj-amount')!.value, p.currency);
          if (!amount.ok) {
            showToast(amount.error, 'warn');
            return null;
          }
          if (!reason) {
            showToast('Reason is required (audit log).', 'warn');
            return null;
          }
          return { mode, creditAmount: amount.minor.toString(), reason };
        },
      });
      if (!parsed) return;
      try {
        const res = await deps.client.adjustPlayerBalance(p.id, parsed);
        const tail =
          res.mode === 'reconcile_manual'
            ? `(reconcile_manual — no integrator call)`
            : `New balance: ${fmtMoney(res.newBalanceCredits ?? '0', res.fiatCurrency || p.currency)} ${res.fiatCurrency || p.currency}${res.integratorTxId ? ` · integratorTx ${res.integratorTxId.slice(0, 12)}…` : ''}`;
        showToast(`${res.mode}: ${tail}`, 'success');
        await refresh();
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
  }

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
